import axios from 'axios';
import { API_BASE_URL } from '../components/config/api';
import { isMigrationPayload, reportMigration } from '../utils/migration';
import { addClientBuildHeader } from '../utils/clientBuild';
import { saveBlob } from '../utils/saveBlob';

// Create axios instance
const api = axios.create({
  baseURL: `${API_BASE_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('xentrix_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return addClientBuildHeader(config);
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle token refresh and errors
api.interceptors.response.use(
  (response) => {
    // Check for new token in response headers
    const newToken = response.headers['x-new-token'];
    if (newToken) {
      localStorage.setItem('xentrix_token', newToken);
    }
    return response;
  },
  (error) => {
    // Ο οργανισμός μεταφέρθηκε σε άλλο server → οθόνη μεταφοράς. ΠΡΙΝ από τον
    // έλεγχο 401: δεν είναι αποσύνδεση, είναι «πήγαινε αλλού».
    if (error.response?.status === 410 && isMigrationPayload(error.response?.data)) {
      reportMigration(error.response.data.new_url);
      return Promise.reject(error);
    }
    if (error.response?.status === 401) {
      // Token expired or invalid
      localStorage.removeItem('xentrix_token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: async (username: string, password: string) => {
    const response = await api.post('/auth/login', { username, password });
    return response.data;
  },
  
  logout: async () => {
    // Note: Token removal is handled by useAuth hook
    const response = await api.post('/auth/logout');
    return response.data;
  },
  
  getMe: async () => {
    const response = await api.get('/auth/me');
    return response.data;
  },
  
  verifyToken: async () => {
    const response = await api.get('/auth/verify');
    return response.data;
  },
};

// «Συσκευές ταμείου» από το διαχειριστικό του καταστήματος (οθόνη πελάτη + modem ανά PC, agent 2.4.0+).
// Ίδιο συμβόλαιο με το supportAPI.getDevices… (backend/routes/cashierDevices.js), άλλη πύλη.
export const cashierDevicesAPI = {
  list: async (storeId: number) => (await api.get(`/stores/${storeId}/cashier-devices`)).data,
  save: async (storeId: number, uid: string, data: { display: unknown; callerId: unknown }) =>
    (await api.put(`/stores/${storeId}/cashier-devices/${uid}`, data)).data,
  cmd: async (storeId: number, uid: string, op: string, args: Record<string, unknown> = {}) =>
    (await api.post(`/stores/${storeId}/cashier-devices/${uid}/cmd`, { ...args, op })).data,
  lastCall: async (storeId: number, since: string) =>
    (await api.get(`/stores/${storeId}/cashier-devices/last-call`, { params: { since } })).data,
};

// Store API
export const storeAPI = {
  getSettings: async (storeId: number) => {
    const response = await api.get(`/stores/${storeId}/settings`);
    return response.data;
  },
  
  updateSettings: async (storeId: number, data: Partial<{
    store_name: string;
    orders_view_preference: 'card' | 'list';
    auto_disable_on_stockout: boolean;
  }>) => {
    const response = await api.put(`/stores/${storeId}/settings`, data);
    return response.data;
  },
  
  getPlatforms: async (storeId: number) => {
    const response = await api.get(`/stores/${storeId}/platforms`);
    return response.data;
  },
  
  updatePlatform: async (storeId: number, platformName: string, data: any) => {
    const response = await api.put(`/stores/${storeId}/platforms/${platformName}`, data);
    return response.data;
  },
};

// Orders API
export const ordersAPI = {
  getOrders: async (storeId: number, params?: any) => {
    const response = await api.get(`/orders/${storeId}`, { params });
    return response.data;
  },

  // Accept order manually with estimated delivery time
  acceptOrder: async (orderId: number, estimatedTime: number) => {
    const response = await api.post(`/orders/${orderId}/accept`, {
      estimatedTime: estimatedTime
    });
    return response.data;
  },

  // Reject order manually
  rejectOrder: async (orderId: number, reason?: string) => {
    const response = await api.post(`/orders/${orderId}/reject`, {
      reason: reason || 'Restaurant cannot fulfill order'
    });
    return response.data;
  },

  // Get order details
  getOrderDetails: async (orderId: number) => {
    const response = await api.get(`/orders/${orderId}`);
    return response.data;
  },

  // Get product statistics
  getProductStats: async (storeId: number, options?: { days?: number; dateFrom?: string; dateTo?: string }) => {
    const params = options || {};
    const response = await api.get(`/orders/${storeId}/products/stats`, { params });
    return response.data;
  },

  // Get analytics data
  getAnalytics: async (storeId: number, options?: { days?: number; dateFrom?: string; dateTo?: string }) => {
    const params = options || {};
    const response = await api.get(`/orders/${storeId}/analytics`, { params });
    return response.data;
  },

  // Get customer analytics
  getCustomerStats: async (storeId: number, options?: { days?: number; dateFrom?: string; dateTo?: string; limit?: number }) => {
    const params = options || {};
    const response = await api.get(`/orders/${storeId}/customers/stats`, { params });
    return response.data;
  }
};

// Products API
export const productsAPI = {
  getCategories: async (storeId: number, params?: { flat?: boolean }) => {
    const queryParams = params?.flat ? '?flat=true' : '';
    const response = await api.get(`/products/stores/${storeId}/categories${queryParams}`);
    const data = response.data;
    return Array.isArray(data) ? data : (data.categories || []);
  },

  createCategory: async (storeId: number, data: { name: string; description?: string; display_order?: number; parent_id?: number | null }) => {
    const response = await api.post(`/products/stores/${storeId}/categories`, data);
    return response.data;
  },

  updateCategory: async (storeId: number, categoryId: number, data: any) => {
    const response = await api.put(`/products/stores/${storeId}/categories/${categoryId}`, data);
    return response.data;
  },

  getProducts: async (storeId: number, params?: { category_id?: number; available_only?: boolean }) => {
    const response = await api.get(`/products/stores/${storeId}/products`, { params });
    const data = response.data;
    return Array.isArray(data) ? data : (data.products || []);
  },

  createProduct: async (storeId: number, data: any) => {
    const response = await api.post(`/products/stores/${storeId}/products`, data);
    return response.data;
  },

  updateProduct: async (productId: number, data: any) => {
    const response = await api.put(`/products/${productId}`, data);
    return response.data;
  },

  toggleAvailability: async (productId: number, isAvailable: boolean) => {
    const response = await api.patch(`/products/${productId}/availability`, { is_available: isAvailable });
    return response.data;
  },

  toggleKioskRecommended: async (productId: number, isRecommended: boolean) => {
    const response = await api.patch(`/products/${productId}/kiosk-recommended`, { is_kiosk_recommended: isRecommended });
    return response.data;
  },

  getIngredients: async (storeId: number, grouped?: boolean) => {
    const params = grouped ? '?grouped=true' : '';
    const response = await api.get(`/products/stores/${storeId}/ingredients${params}`);
    return response.data;
  },

  getIngredientCategories: async (storeId: number) => {
    const response = await api.get(`/products/stores/${storeId}/ingredient-categories`);
    return response.data;
  },

  getStoreIngredients: async (storeId: number) => {
    const response = await api.get(`/stores/${storeId}/store-ingredients`);
    return response.data;
  },
};

// Laas Mile (Efood) Delivery API
export const laasMileAPI = {
  // Get store config
  getConfig: async (storeId: number) => {
    const response = await api.get(`/laas-mile/${storeId}/config`);
    return response.data;
  },
  
  // Update store config
  updateConfig: async (storeId: number, config: {
    laas_mile_enabled?: boolean;
    laas_mile_client_id?: string;
    laas_mile_key_id?: string;
    laas_mile_scope?: string;
    laas_mile_private_key?: string;
  }) => {
    const response = await api.put(`/laas-mile/${storeId}/config`, config);
    return response.data;
  },
  
  // Check if order can use Laas Mile
  checkEligibility: async (storeId: number, orderId: number) => {
    const response = await api.get(`/laas-mile/${storeId}/check/${orderId}`);
    return response.data;
  },
  
  // Estimate delivery fee
  estimateFee: async (storeId: number, data: {
    customer_address: string;
    customer_latitude: number;
    customer_longitude: number;
    total_amount: number;
    payment_type: string;
  }) => {
    const response = await api.post(`/laas-mile/${storeId}/estimate-fee`, data);
    return response.data;
  },
  
  // Estimate delivery time
  estimateTime: async (storeId: number, data: {
    customer_address: string;
    customer_latitude: number;
    customer_longitude: number;
    total_amount: number;
    payment_type: string;
  }) => {
    const response = await api.post(`/laas-mile/${storeId}/estimate-time`, data);
    return response.data;
  },
  
  // Create delivery
  createDelivery: async (storeId: number, orderId: number) => {
    const response = await api.post(`/laas-mile/${storeId}/create/${orderId}`);
    return response.data;
  },
  
  // Get delivery status
  getStatus: async (storeId: number, orderId: number) => {
    const response = await api.get(`/laas-mile/${storeId}/status/${orderId}`);
    return response.data;
  },
  
  // Get courier location
  getCourierLocation: async (storeId: number, orderId: number) => {
    const response = await api.get(`/laas-mile/${storeId}/courier-location/${orderId}`);
    return response.data;
  },
  
  // Cancel delivery
  cancelDelivery: async (storeId: number, orderId: number, reason?: string) => {
    const response = await api.post(`/laas-mile/${storeId}/cancel/${orderId}`, { reason });
    return response.data;
  }
};

// Wolt Drive API
export const woltDriveAPI = {
  // Get store config
  getConfig: async (storeId: number) => {
    const response = await api.get(`/wolt-drive/${storeId}/config`);
    return response.data;
  },
  
  // Update store config
  updateConfig: async (storeId: number, config: {
    wolt_drive_enabled?: boolean;
    wolt_drive_venue_id?: string;
    wolt_drive_api_key?: string;
  }) => {
    const response = await api.put(`/wolt-drive/${storeId}/config`, config);
    return response.data;
  },
  
  // Check if order can use Wolt Drive
  checkEligibility: async (storeId: number, orderId: number) => {
    const response = await api.get(`/wolt-drive/${storeId}/check/${orderId}`);
    return response.data;
  },
  
  // Get shipment promise (estimate)
  getEstimate: async (storeId: number, dropoff: {
    street: string;
    city: string;
    post_code?: string;
    lat?: number;
    lon?: number;
  }) => {
    const response = await api.post(`/wolt-drive/${storeId}/estimate`, dropoff);
    return response.data;
  },
  
  // Create delivery
  createDelivery: async (storeId: number, orderId: number, data: {
    promise_id?: string;
    recipient_name?: string;
    recipient_phone?: string;
    comment?: string;
    dropoff?: {
      street: string;
      city: string;
      post_code?: string;
      lat?: number;
      lon?: number;
    };
  }) => {
    const response = await api.post(`/wolt-drive/${storeId}/create/${orderId}`, data);
    return response.data;
  },
  
  // Get delivery status
  getStatus: async (storeId: number, orderId: number) => {
    const response = await api.get(`/wolt-drive/${storeId}/status/${orderId}`);
    return response.data;
  },
  
  // Cancel delivery
  cancelDelivery: async (storeId: number, orderId: number) => {
    const response = await api.post(`/wolt-drive/${storeId}/cancel/${orderId}`);
    return response.data;
  }
};
  
const agentFleetBase = (orgId: number | null) => (orgId === null ? '/print-agent/unassigned' : `/organizations/${orgId}/print-agent`);

// Organization API (multi-store chains)
export const organizationAPI = {
  // List orgs the employee has access to
  list: async () => {
    const response = await api.get('/organizations');
    return response.data;
  },
  // Create org
  create: async (data: { name: string; slug: string; logo_url?: string; contact_email?: string; contact_phone?: string }) => {
    const response = await api.post('/organizations', data);
    return response.data;
  },
  // Get org details (stores + members)
  get: async (orgId: number) => {
    const response = await api.get(`/organizations/${orgId}`);
    return response.data;
  },
  // Update org
  update: async (orgId: number, data: any) => {
    const response = await api.put(`/organizations/${orgId}`, data);
    return response.data;
  },
  // Εμφάνιση: προεπιλογή όλων των καταστημάτων (migration 263). null = προεπιλογή εφαρμογής.
  updateTheme: async (
    orgId: number,
    theme: { brand?: string | null; surface?: string | null; appearance?: string | null } | null,
  ) => {
    const response = await api.put(`/organizations/${orgId}/theme`, { theme });
    return response.data as { theme: { brand?: string; surface?: string; appearance?: string } | null };
  },
  // Delete org
  delete: async (orgId: number) => {
    const response = await api.delete(`/organizations/${orgId}`);
    return response.data;
  },
  // Add store to org
  addStore: async (orgId: number, storeId: number, priceListId?: number | null) => {
    const response = await api.post(`/organizations/${orgId}/stores`, { store_id: storeId, price_list_id: priceListId ?? null });
    return response.data;
  },
  // Remove store from org
  removeStore: async (orgId: number, storeId: number) => {
    const response = await api.delete(`/organizations/${orgId}/stores/${storeId}`);
    return response.data;
  },
  // ===== Συμφωνία καταλόγου (κεντρικός ↔ καταστήματα) =====
  // fresh: παρακάμπτει την προσωρινή μνήμη του διακομιστή (κουμπί «Ανανέωση»).
  // includeLow: δείχνει και τις ασήμαντες διαφορές (περιγραφή, εικόνα, σειρά).
  getCatalogConsistency: async (orgId: number, opts: { fresh?: boolean; includeLow?: boolean } = {}) => {
    const qs = new URLSearchParams();
    if (opts.fresh) qs.set('fresh', '1');
    if (opts.includeLow) qs.set('includeLow', '1');
    const response = await api.get(`/organizations/${orgId}/catalog/consistency${qs.toString() ? `?${qs}` : ''}`);
    return response.data;
  },
  // Οι ενέργειες κλειδώνουν στο org_product_id — εκτός από τα τοπικά προϊόντα,
  // που δεν έχουν κεντρική ταυτότητα και αναγνωρίζονται από το δικό τους product_id.
  applyCatalogConsistency: async (orgId: number, actions: Array<{ kind: string; store_id: number; org_product_id?: number; product_id?: number; fields?: string[] }>) => {
    const response = await api.post(`/organizations/${orgId}/catalog/consistency/apply`, { actions });
    return response.data;
  },
  // Εγκρίνει από τα κεντρικά τις αλλαγές που περιμένουν έγκριση στα καταστήματα.
  // Χωρίς store_ids = όλα τα καταστήματα του οργανισμού.
  approvePendingSync: async (orgId: number, storeIds?: number[]) => {
    const response = await api.post(`/organizations/${orgId}/catalog/consistency/approve-pending`,
      storeIds && storeIds.length ? { store_ids: storeIds } : {});
    return response.data;
  },

  // ===== Αυτο-αναβάθμιση print agent (οθόνη «Εκδόσεις agent») =====
  // orgId null = τα καταστήματα ΧΩΡΙΣ οργανισμό (π.χ. GENERIS στον 144).
  getAgentFleet: async (orgId: number | null) => {
    const response = await api.get(`${agentFleetBase(orgId)}/fleet`);
    return response.data;
  },
  setAgentRollout: async (orgId: number | null, storeIds: number[], version: string) => {
    const response = await api.post(`${agentFleetBase(orgId)}/rollout`, { storeIds, version });
    return response.data;
  },
  clearAgentRollout: async (orgId: number | null, storeIds: number[]) => {
    const response = await api.post(`${agentFleetBase(orgId)}/rollout/clear`, { storeIds });
    return response.data;
  },
  // Δημοσίευση νέας έκδοσης από τον κώδικα του server — μόνο super-admin.
  getAgentReleaseDraft: async () => {
    const response = await api.get(`/print-agent/releases/draft`);
    return response.data;
  },
  publishAgentRelease: async (commit: string, notes: string) => {
    const response = await api.post(`/print-agent/releases`, { commit, notes });
    return response.data;
  },
  // Γενικός διακόπτης — κοινός για όλους τους πελάτες, μόνο super-admin.
  setAgentAutoUpdate: async (enabled: boolean) => {
    const response = await api.put(`/print-agent/auto-update`, { enabled });
    return response.data;
  },

  // ===== Price lists (τιμοκατάλογοι) =====
  getPriceLists: async (orgId: number) => {
    const response = await api.get(`/organizations/${orgId}/catalog/price-lists`);
    return response.data;
  },
  createPriceList: async (orgId: number, data: any) => {
    const response = await api.post(`/organizations/${orgId}/catalog/price-lists`, data);
    return response.data;
  },
  updatePriceList: async (orgId: number, id: number, data: any) => {
    const response = await api.put(`/organizations/${orgId}/catalog/price-lists/${id}`, data);
    return response.data;
  },
  deletePriceList: async (orgId: number, id: number) => {
    const response = await api.delete(`/organizations/${orgId}/catalog/price-lists/${id}`);
    return response.data;
  },
  getPriceListPrices: async (orgId: number, id: number) => {
    const response = await api.get(`/organizations/${orgId}/catalog/price-lists/${id}/prices`);
    return response.data;
  },
  // listId = number or 'default' (catalog size prices). Τιμή ΠΡΟΪΟΝΤΟΣ σε τιμοκατάλογο δεν
  // υπάρχει πια (Φ5): η τιμή ζει στο μέγεθος, το τέλος στο state_fees — ο server απαντά 410.
  setSizeListPrice: async (orgId: number, listId: number | 'default', sizeId: number, prices: any) => {
    const response = await api.put(`/organizations/${orgId}/catalog/price-lists/${listId}/size-price/${sizeId}`, prices);
    return response.data;
  },
  assignStorePriceList: async (orgId: number, storeId: number, priceListId: number | null) => {
    const response = await api.put(`/organizations/${orgId}/catalog/stores/${storeId}/price-list`, { price_list_id: priceListId });
    return response.data;
  },
  bulkSetPriceList: async (orgId: number, listId: number, percent: number) => {
    const response = await api.post(`/organizations/${orgId}/catalog/price-lists/${listId}/bulk`, { percent });
    return response.data;
  },
  // Get members
  getMembers: async (orgId: number) => {
    const response = await api.get(`/organizations/${orgId}/members`);
    return response.data;
  },
  // Add member (org_user). storeIds: null/[] = all stores, int[] = scoped subset
  addMember: async (orgId: number, orgUserId: number, role: string, storeIds?: number[] | null) => {
    const response = await api.post(`/organizations/${orgId}/members`, {
      org_user_id: orgUserId, role, store_ids: storeIds ?? null,
    });
    return response.data;
  },
  // ΝΕΟ μέλος (15/10/2026): φτιάχνει τον χρήστη οργανισμού και τον προσθέτει, σε μία συναλλαγή.
  createMember: async (orgId: number, member: {
    first_name: string; last_name: string; email: string; phone?: string; password: string; role: string; store_ids?: number[] | null;
  }) => {
    const response = await api.post(`/organizations/${orgId}/members/new`, { ...member, store_ids: member.store_ids ?? null });
    return response.data;
  },
  // Update member role and/or store scope (store_ids undefined = leave as-is)
  updateMemberRole: async (orgId: number, orgUserId: number, role: string, storeIds?: number[] | null) => {
    const body: any = { role };
    if (storeIds !== undefined) body.store_ids = storeIds;
    const response = await api.put(`/organizations/${orgId}/members/${orgUserId}`, body);
    return response.data;
  },
  // Remove member
  removeMember: async (orgId: number, orgUserId: number) => {
    const response = await api.delete(`/organizations/${orgId}/members/${orgUserId}`);
    return response.data;
  },
  // Switch store context
  switchStore: async (orgId: number, storeId: number) => {
    const response = await api.post(`/organizations/${orgId}/switch-store`, { store_id: storeId });
    return response.data;
  },
  // Exit a store an org user switched into — returns a plain org token (no active store)
  exitStore: async () => {
    const response = await api.post(`/organizations/exit-store`, {});
    return response.data;
  },
  // Get org dashboard stats
  dashboard: async (orgId: number) => {
    const response = await api.get(`/organizations/${orgId}/dashboard`);
    return response.data;
  },
  // Get deep, date-ranged org analytics aggregated across all stores.
  // storeIds (optional) isolates specific stores; empty/undefined = all stores.
  analytics: async (orgId: number, from?: string, to?: string, storeIds?: number[]) => {
    const store_ids = storeIds && storeIds.length ? storeIds.join(',') : undefined;
    const response = await api.get(`/organizations/${orgId}/analytics`, { params: { from, to, store_ids } });
    return response.data;
  },
  // Full report data (accounting/products/channels) aggregated across the org's
  // scoped + filtered stores — feeds the configurable PDF/Excel report exporter.
  reportData: async (orgId: number, from: string, to: string, storeIds: number[] | undefined, sections: string, detail: string) => {
    const store_ids = storeIds && storeIds.length ? storeIds.join(',') : undefined;
    const response = await api.get(`/organizations/${orgId}/report-data`, { params: { from, to, store_ids, sections, detail } });
    return response.data;
  },
  // Download the transaction × product Excel report (date range, optional store filter).
  // Returns the raw blob response; the caller triggers the browser download.
  // On a non-2xx the error body arrives as a Blob — read it as text for the message.
  exportTransactionProducts: async (orgId: number, from: string, to: string, storeIds?: number[]) => {
    const store_ids = storeIds && storeIds.length ? storeIds.join(',') : undefined;
    return api.get(`/organizations/${orgId}/reports/transaction-products.xlsx`, {
      params: { from, to, store_ids },
      responseType: 'blob',
    });
  },
  // Set a friendly per-branch label on a store (falls back to legal name when empty)
  updateStoreLabel: async (orgId: number, storeId: number, display_name: string | null) => {
    const response = await api.put(`/organizations/${orgId}/stores/${storeId}`, { display_name });
    return response.data;
  },
  // --- HQ visibility + central-only cancellation of a store's orders / fiscal documents ---
  storeOrders: async (orgId: number, storeId: number, from?: string, to?: string, status?: string, includeFailed?: boolean) => {
    const response = await api.get(`/organizations/${orgId}/stores/${storeId}/orders`, { params: { from, to, status, include_failed: includeFailed ? 1 : undefined } });
    return response.data;
  },
  storeFiscal: async (orgId: number, storeId: number, from?: string, to?: string, docType?: string) => {
    const response = await api.get(`/organizations/${orgId}/stores/${storeId}/fiscal`, {
      params: { from, to, doc_type: docType && docType !== 'all' ? docType : undefined },
    });
    return response.data;
  },
  // «Εξαγωγή σε Excel» της λίστας παραστατικών: ΙΔΙΑ φίλτρα με το storeFiscal, όλες οι γραμμές (.xlsx).
  // Επιστρέφει την απάντηση blob· σε σφάλμα το σώμα έρχεται κι αυτό ως Blob (JSON με error/code).
  exportStoreFiscal: async (orgId: number, storeId: number, from?: string, to?: string, docType?: string) => {
    return api.get(`/organizations/${orgId}/stores/${storeId}/fiscal/export.xlsx`, {
      params: { from, to, doc_type: docType && docType !== 'all' ? docType : undefined },
      responseType: 'blob',
    });
  },
  // Πλήρες παραστατικό (κεφαλίδα + πελάτης + γραμμές προϊόντων)
  storeFiscalDetail: async (orgId: number, storeId: number, documentId: number) => {
    const response = await api.get(`/organizations/${orgId}/stores/${storeId}/fiscal/${documentId}`);
    return response.data;
  },
  // Πλήρης παραγγελία (κεφαλίδα + πελάτης + είδη + παραστατικά)
  storeOrderDetail: async (orgId: number, storeId: number, orderId: number) => {
    const response = await api.get(`/organizations/${orgId}/stores/${storeId}/orders/${orderId}`);
    return response.data;
  },
  cancelStoreOrder: async (orgId: number, storeId: number, orderId: number, reason: string, force?: boolean) => {
    const response = await api.post(`/organizations/${orgId}/stores/${storeId}/orders/${orderId}/cancel`, { reason, force });
    return response.data;
  },
  cancelStoreFiscal: async (orgId: number, storeId: number, documentId: number) => {
    const response = await api.post(`/organizations/${orgId}/stores/${storeId}/fiscal/${documentId}/cancel`, {});
    return response.data;
  },
  // --- Store & user credentials (view usernames; reset password / PIN, optionally random) ---
  storeCredentials: async (orgId: number, storeId: number) => {
    const response = await api.get(`/organizations/${orgId}/stores/${storeId}/credentials`);
    return response.data;
  },
  resetStorePassword: async (orgId: number, storeId: number, password?: string) => {
    const response = await api.post(`/organizations/${orgId}/stores/${storeId}/reset-password`, { password });
    return response.data;
  },
  resetEmployeePin: async (orgId: number, storeId: number, empId: number, pin?: string) => {
    const response = await api.post(`/organizations/${orgId}/stores/${storeId}/employees/${empId}/reset-pin`, { pin });
    return response.data;
  },
  // --- Org Users (admin accounts, not employees) ---
  listOrgUsers: async () => {
    const response = await api.get('/organizations/users/all');
    return response.data;
  },
  createOrgUser: async (data: { first_name: string; last_name: string; email: string; password: string; phone?: string }) => {
    const response = await api.post('/organizations/users', data);
    return response.data;
  },
  updateOrgUser: async (userId: number, data: any) => {
    const response = await api.put(`/organizations/users/${userId}`, data);
    return response.data;
  },
  deleteOrgUser: async (userId: number) => {
    const response = await api.delete(`/organizations/users/${userId}`);
    return response.data;
  },
  // --- Org Catalog ---
  getCatalogCategories: async (orgId: number) => {
    const response = await api.get(`/organizations/${orgId}/catalog/categories`);
    return response.data;
  },
  createCatalogCategory: async (orgId: number, data: any) => {
    const response = await api.post(`/organizations/${orgId}/catalog/categories`, data);
    return response.data;
  },
  updateCatalogCategory: async (orgId: number, catId: number, data: any) => {
    const response = await api.put(`/organizations/${orgId}/catalog/categories/${catId}`, data);
    return response.data;
  },
  deleteCatalogCategory: async (orgId: number, catId: number) => {
    const response = await api.delete(`/organizations/${orgId}/catalog/categories/${catId}`);
    return response.data;
  },
  // ── Κεντρικές προσφορές: μία εγγραφή που ισχύει σε όλα τα καταστήματα ──
  getPromotions: async (orgId: number) => {
    const response = await api.get(`/organizations/${orgId}/promotions`);
    return response.data;
  },
  createPromotion: async (orgId: number, data: any) => {
    const response = await api.post(`/organizations/${orgId}/promotions`, data);
    return response.data;
  },
  updatePromotion: async (orgId: number, id: number, data: any) => {
    const response = await api.put(`/organizations/${orgId}/promotions/${id}`, data);
    return response.data;
  },
  deletePromotion: async (orgId: number, id: number) => {
    const response = await api.delete(`/organizations/${orgId}/promotions/${id}`);
    return response.data;
  },
  getPromotionAnalytics: async (orgId: number, from?: string, to?: string) => {
    const q = [from && `from=${from}`, to && `to=${to}`].filter(Boolean).join('&');
    const response = await api.get(`/organizations/${orgId}/promotions/analytics${q ? '?' + q : ''}`);
    return response.data;
  },
  // ── Προτάσεις πώλησης (upsell): κεντρικές, με έναν διακόπτη για όλο τον οργανισμό ──
  getUpsell: async (orgId: number) => {
    const response = await api.get(`/organizations/${orgId}/upsell`);
    return response.data;
  },
  setUpsellEnabled: async (orgId: number, enabled: boolean) => {
    const response = await api.put(`/organizations/${orgId}/upsell/settings`, { enabled });
    return response.data;
  },
  createUpsellSuggestion: async (orgId: number, data: any) => {
    const response = await api.post(`/organizations/${orgId}/upsell/suggestions`, data);
    return response.data;
  },
  updateUpsellSuggestion: async (orgId: number, id: number, data: any) => {
    const response = await api.put(`/organizations/${orgId}/upsell/suggestions/${id}`, data);
    return response.data;
  },
  // Σβήνει ΚΑΙ τα στατιστικά της πρότασης (cascade) — το UI προτείνει απενεργοποίηση.
  deleteUpsellSuggestion: async (orgId: number, id: number) => {
    const response = await api.delete(`/organizations/${orgId}/upsell/suggestions/${id}`);
    return response.data;
  },
  getUpsellReport: async (orgId: number, from: string, to: string, storeIds?: number[]) => {
    const q = [`from=${from}`, `to=${to}`, storeIds && storeIds.length > 0 && `store_ids=${storeIds.join(',')}`]
      .filter(Boolean).join('&');
    const response = await api.get(`/organizations/${orgId}/upsell/report?${q}`);
    return response.data;
  },

  getCatalogProducts: async (orgId: number, categoryId?: number) => {
    const params = categoryId ? `?category_id=${categoryId}` : '';
    const response = await api.get(`/organizations/${orgId}/catalog/products${params}`);
    return response.data;
  },
  getCatalogProduct: async (orgId: number, productId: number) => {
    const response = await api.get(`/organizations/${orgId}/catalog/products/${productId}`);
    return response.data;
  },
  createCatalogProduct: async (orgId: number, data: any) => {
    const response = await api.post(`/organizations/${orgId}/catalog/products`, data);
    return response.data;
  },
  updateCatalogProduct: async (orgId: number, productId: number, data: any) => {
    const response = await api.put(`/organizations/${orgId}/catalog/products/${productId}`, data);
    return response.data;
  },
  deleteCatalogProduct: async (orgId: number, productId: number) => {
    const response = await api.delete(`/organizations/${orgId}/catalog/products/${productId}`);
    return response.data;
  },
  // --- Sync Requests ---
  getOrgSyncRequests: async (orgId: number, status?: string) => {
    const params = status ? `?status=${status}` : '';
    const response = await api.get(`/organizations/${orgId}/catalog/sync-requests${params}`);
    return response.data;
  },
  getPendingSyncRequests: async (storeId: number) => {
    const response = await api.get(`/organizations/sync/store/${storeId}/pending`);
    return response.data;
  },
  getSyncHistory: async (storeId: number) => {
    const response = await api.get(`/organizations/sync/store/${storeId}/history`);
    return response.data;
  },
  approveSyncRequest: async (requestId: number) => {
    const response = await api.post(`/organizations/sync/requests/${requestId}/approve`);
    return response.data;
  },
  rejectSyncRequest: async (requestId: number) => {
    const response = await api.post(`/organizations/sync/requests/${requestId}/reject`);
    return response.data;
  },
  approveAllSyncRequests: async (storeId: number) => {
    const response = await api.post(`/organizations/sync/store/${storeId}/approve-all`);
    return response.data;
  },
  syncToStore: async (orgId: number, storeId: number) => {
    const response = await api.post(`/organizations/${orgId}/catalog/sync-to-store/${storeId}`);
    return response.data;
  },
  importFromStore: async (orgId: number, storeId: number) => {
    const response = await api.post(`/organizations/${orgId}/catalog/import-from-store/${storeId}`);
    return response.data;
  },
  batchUpdateProducts: async (orgId: number, productIds: number[], updates: any, targetStoreIds?: number[]) => {
    const response = await api.put(`/organizations/${orgId}/catalog/products/batch`, {
      product_ids: productIds,
      updates,
      target_store_ids: targetStoreIds || null
    });
    return response.data;
  },
  getOrgStores: async (orgId: number) => {
    const response = await api.get(`/organizations/${orgId}/stores`);
    return response.data;
  },
  // Size templates
  getSizeTemplates: async (orgId: number) => {
    const response = await api.get(`/organizations/${orgId}/catalog/size-templates`);
    return response.data;
  },
  createSizeTemplate: async (orgId: number, data: any) => {
    const response = await api.post(`/organizations/${orgId}/catalog/size-templates`, data);
    return response.data;
  },
  createSizeTemplateFromProduct: async (orgId: number, productId: number, name?: string) => {
    const response = await api.post(`/organizations/${orgId}/catalog/size-templates/from-product`, { product_id: productId, name });
    return response.data;
  },
  updateSizeTemplate: async (orgId: number, templateId: number, data: any) => {
    const response = await api.put(`/organizations/${orgId}/catalog/size-templates/${templateId}`, data);
    return response.data;
  },
  deleteSizeTemplate: async (orgId: number, templateId: number) => {
    const response = await api.delete(`/organizations/${orgId}/catalog/size-templates/${templateId}`);
    return response.data;
  },
  applySizeTemplate: async (orgId: number, productId: number, templateId: number) => {
    const response = await api.post(`/organizations/${orgId}/catalog/products/${productId}/apply-size-template/${templateId}`);
    return response.data;
  },
  reapplySizeTemplate: async (orgId: number, templateId: number, targetStoreIds?: number[]) => {
    const response = await api.post(`/organizations/${orgId}/catalog/size-templates/${templateId}/reapply`, {
      target_store_ids: targetStoreIds || null
    });
    return response.data;
  },
  // Ingredient templates
  getIngredientTemplates: async (orgId: number) => {
    const response = await api.get(`/organizations/${orgId}/catalog/ingredient-templates`);
    return response.data;
  },
  createIngredientTemplate: async (orgId: number, data: any) => {
    const response = await api.post(`/organizations/${orgId}/catalog/ingredient-templates`, data);
    return response.data;
  },
  updateIngredientTemplate: async (orgId: number, templateId: number, data: any) => {
    const response = await api.put(`/organizations/${orgId}/catalog/ingredient-templates/${templateId}`, data);
    return response.data;
  },
  deleteIngredientTemplate: async (orgId: number, templateId: number) => {
    const response = await api.delete(`/organizations/${orgId}/catalog/ingredient-templates/${templateId}`);
    return response.data;
  },
  applyIngredientTemplate: async (orgId: number, productId: number, templateId: number) => {
    const response = await api.post(`/organizations/${orgId}/catalog/products/${productId}/apply-ingredient-template/${templateId}`);
    return response.data;
  },
  reapplyIngredientTemplate: async (orgId: number, templateId: number, targetStoreIds?: number[]) => {
    const response = await api.post(`/organizations/${orgId}/catalog/ingredient-templates/${templateId}/reapply`, {
      target_store_ids: targetStoreIds || null
    });
    return response.data;
  },
  getOptionTemplates: async (orgId: number) => {
    const response = await api.get(`/organizations/${orgId}/catalog/option-templates`);
    return response.data;
  },
  createOptionTemplate: async (orgId: number, data: any) => {
    const response = await api.post(`/organizations/${orgId}/catalog/option-templates`, data);
    return response.data;
  },
  updateOptionTemplate: async (orgId: number, templateId: number, data: any) => {
    const response = await api.put(`/organizations/${orgId}/catalog/option-templates/${templateId}`, data);
    return response.data;
  },
  deleteOptionTemplate: async (orgId: number, templateId: number) => {
    const response = await api.delete(`/organizations/${orgId}/catalog/option-templates/${templateId}`);
    return response.data;
  },
  reapplyOptionTemplate: async (orgId: number, templateId: number, targetStoreIds?: number[]) => {
    const response = await api.post(`/organizations/${orgId}/catalog/option-templates/${templateId}/reapply`, { target_store_ids: targetStoreIds || [] });
    return response.data;
  },
  applyOptionTemplate: async (orgId: number, templateId: number, productIds: number[], targetStoreIds?: number[]) => {
    const response = await api.post(`/organizations/${orgId}/catalog/option-templates/${templateId}/apply`, { product_ids: productIds, target_store_ids: targetStoreIds || [] });
    return response.data;
  },
  templatizeOptionGroup: async (orgId: number, groupId: number, linkAll = true) => {
    const response = await api.post(`/organizations/${orgId}/catalog/option-templates/from-group`, { group_id: groupId, link_all: linkAll });
    return response.data;
  },
  templatizeAllOptionGroups: async (orgId: number) => {
    const response = await api.post(`/organizations/${orgId}/catalog/option-templates/templatize-all`, {});
    return response.data;
  },
  // Εγχειρίδιο χρήσης οργανισμού (PDF) — το κατεβάζουν τα ταμεία από το μενού
  getManual: async (orgId: number): Promise<{ manual: ManualInfo | null }> => {
    const response = await api.get(`/organizations/${orgId}/manual`);
    return response.data;
  },
  uploadManual: async (orgId: number, file: File): Promise<{ manual: ManualInfo }> => {
    const response = await api.put(`/organizations/${orgId}/manual`, file, {
      headers: { 'Content-Type': 'application/pdf', 'X-File-Name': encodeURIComponent(file.name) },
      timeout: 120000,
    });
    return response.data;
  },
  deleteManual: async (orgId: number) => {
    const response = await api.delete(`/organizations/${orgId}/manual`);
    return response.data;
  },
  downloadManual: async (orgId: number, fileName: string) => {
    const response = await api.get(`/organizations/${orgId}/manual/download`, { responseType: 'blob' });
    saveBlob(response.data, fileName);
  },
};

// Dine-In API
export const dineInAPI = {
  // Zones & Tables
  getZones: async (storeId: number) => {
    const response = await api.get(`/dine-in/${storeId}/zones`);
    return response.data;
  },
  getTables: async (storeId: number, zoneId: number) => {
    const response = await api.get(`/dine-in/${storeId}/zones/${zoneId}/tables`);
    return response.data;
  },
  getTableDetails: async (storeId: number, tableId: number) => {
    const response = await api.get(`/dine-in/${storeId}/tables/${tableId}/details`);
    return response.data;
  },
  updateTableStatus: async (storeId: number, tableId: number, status: string) => {
    const response = await api.put(`/dine-in/${storeId}/tables/${tableId}/status`, { status });
    return response.data;
  },

  // Settings
  getSettings: async (storeId: number) => {
    const response = await api.get(`/dine-in/${storeId}/settings`);
    return response.data;
  },

  // Sessions
  createSession: async (storeId: number, data: { table_id: number; guest_count: number }) => {
    const response = await api.post(`/dine-in/${storeId}/sessions`, data);
    return response.data;
  },
  getSessionDetails: async (storeId: number, sessionId: number) => {
    const response = await api.get(`/dine-in/${storeId}/sessions/${sessionId}/details`);
    return response.data;
  },
  getSessionHistory: async (storeId: number) => {
    const response = await api.get(`/dine-in/${storeId}/sessions/history/today`);
    return response.data;
  },
  cancelSession: async (storeId: number, sessionId: number, reason?: string) => {
    const response = await api.post(`/dine-in/${storeId}/sessions/${sessionId}/cancel`, { reason });
    return response.data;
  },
  moveSession: async (storeId: number, sessionId: number, targetTableId: number) => {
    const response = await api.post(`/dine-in/${storeId}/sessions/${sessionId}/move`, { target_table_id: targetTableId });
    return response.data;
  },

  // Session Discount
  applyDiscount: async (storeId: number, sessionId: number, data: { discount_type: string; discount_value: number }) => {
    const response = await api.post(`/dine-in/${storeId}/sessions/${sessionId}/discount`, data);
    return response.data;
  },
  removeDiscount: async (storeId: number, sessionId: number) => {
    const response = await api.delete(`/dine-in/${storeId}/sessions/${sessionId}/discount`);
    return response.data;
  },

  // Orders
  createOrder: async (storeId: number, sessionId: number, data: { items: any[]; employee_id?: number }) => {
    const response = await api.post(`/dine-in/${storeId}/sessions/${sessionId}/orders`, data);
    return response.data;
  },

  // Order Items
  deleteOrderItem: async (storeId: number, itemId: number) => {
    const response = await api.delete(`/dine-in/${storeId}/order-items/${itemId}`);
    return response.data;
  },
  moveOrderItem: async (storeId: number, itemId: number, targetSessionId: number) => {
    const response = await api.post(`/dine-in/${storeId}/order-items/${itemId}/move`, { target_session_id: targetSessionId });
    return response.data;
  },
  treatItem: async (storeId: number, itemId: number) => {
    const response = await api.post(`/dine-in/${storeId}/order-items/${itemId}/treat`);
    return response.data;
  },
  untreatItem: async (storeId: number, itemId: number) => {
    const response = await api.delete(`/dine-in/${storeId}/order-items/${itemId}/treat`);
    return response.data;
  },

  // Payments
  cashPayment: async (storeId: number, sessionId: number, data: any) => {
    const response = await api.post(`/dine-in/${storeId}/sessions/${sessionId}/cash-payment`, data);
    return response.data;
  },
  cardPayment: async (storeId: number, sessionId: number, data: any) => {
    const response = await api.post(`/dine-in/${storeId}/sessions/${sessionId}/card-payment`, data);
    return response.data;
  },

  // Reservations
  getTodayReservations: async (storeId: number) => {
    const response = await api.get(`/dine-in/${storeId}/reservations/today`);
    return response.data;
  },
};

// POS Terminals API
export const posAPI = {
  getTerminals: async (storeId: number) => {
    const response = await api.get(`/stores/${storeId}/pos/terminals`);
    return response.data;
  },
};

// Fiscal / AADE API
export const fiscalAPI = {
  lookupVat: async (storeId: number, vatNumber: string) => {
    const response = await api.get(`/stores/${storeId}/aade/lookup/${vatNumber}`);
    return response.data;
  },
};

export interface ManualInfo {
  id: number;
  file_name: string;
  size_bytes: number;
  uploaded_by: string | null;
  uploaded_at: string;
}

// Αποθήκευση αρχείου που ήρθε ως blob — ζει στο utils/saveBlob (χωρίς axios), εδώ μένει για τους παλιούς καλούντες.
export { saveBlob };

// Τρέχουσα συνεδρία ταμείου (backend/routes/me.js)
export const meAPI = {
  getPreferences: async (): Promise<{ scope: 'employee' | 'device'; preferences: Record<string, any> }> => {
    const response = await api.get('/me/preferences');
    return response.data;
  },
  saveMenuPins: async (pins: string[] | null) => {
    const response = await api.patch('/me/preferences', { menu_pins: pins });
    return response.data;
  },
  getManual: async (): Promise<{ manual: ManualInfo | null }> => {
    const response = await api.get('/me/manual');
    return response.data;
  },
  downloadManual: async (fileName: string) => {
    const response = await api.get('/me/manual/download', { responseType: 'blob' });
    saveBlob(response.data, fileName);
  },
};

export default api;

